import { readCompletionChunks } from 'config/buddy/completion';
import { BUDDY_CONFIG } from 'config/buddy/config';
import { clients } from 'lib/https';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useOnboardingStore } from 'state/store/onboarding/use-onboarding-store';
import { getConfigurationSettings } from 'services/configuration-service';
const FAILURE_TYPES = new Set([
  'ERROR',
  'SERVER_ERROR',
  'STREAM_CANCELLED',
  'CONTENT_FILTER_VIOLATION',
  'AUTH_REQUIRED',
  'APPROVAL_REQUIRED',
]);
// Smart suggestions are generated after the answer, so the reply is complete once they (or the end) arrive.
const ANSWER_DONE_TYPES = new Set(['END_OF_STREAM', 'SMART_SUGGESTIONS']);

// Callers expect one JSON object, so a closed object is the whole answer; the agent's
// follow-up work (suggestions, analytics) can take seconds more and is not needed.
const isCompleteJsonObject = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

/** Isolated completion transport; fresh requests never read or persist session IDs. */
export async function requestOnboardingCompletion(
  content: string,
  {
    signal,
    reuseSession = true,
    timeoutMs = 30000,
  }: {
    signal?: AbortSignal;
    reuseSession?: boolean;
    timeoutMs?: number;
  } = {}
): Promise<string> {
  if (signal?.aborted) throw new DOMException('Onboarding request aborted', 'AbortError');
  const userId = useCommonStore.getState().user?.id;
  const initialRevision = userId
    ? useOnboardingStore.getState().completionSessionsByUser[userId]?.revision
    : undefined;
  let identityChanged = false;
  const unsubscribe = useCommonStore.subscribe((state) => {
    if (state.user?.id !== userId) identityChanged = true;
  });
  const controller = new AbortController();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let expired = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      expired = true;
      controller.abort();
      reject(new Error('Onboarding extraction timed out'));
    }, timeoutMs);
  });
  let rejectAbort!: (reason: Error) => void;
  const cancelled = new Promise<never>((_, reject) => {
    rejectAbort = reject;
  });
  const onAbort = () => {
    expired = true;
    rejectAbort(new DOMException('Onboarding request aborted', 'AbortError'));
    controller.abort();
  };
  signal?.addEventListener('abort', onAbort, { once: true });
  const bounded = <T>(operation: Promise<T>): Promise<T> =>
    Promise.race([operation, timeout, cancelled]);
  try {
    const config = await bounded(getConfigurationSettings());
    const store = useOnboardingStore.getState();
    let session =
      reuseSession &&
      userId &&
      !identityChanged &&
      store.completionSessionsByUser[userId]?.revision === initialRevision
        ? store.beginCompletionSession(userId, config.blocksKey)
        : undefined;
    // CompletionRequest supports isStream, not response_format (or Buddy's isStreaming).
    const open = () =>
      bounded(
        clients
          .stream(
            `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.collaborationCompletion}`,
            config.blocksKey,
            JSON.stringify({
              isStream: true,
              ...(session?.conversationId
                ? { conversationId: session.conversationId }
                : { isTemporary: true, agentId: BUDDY_CONFIG.agentId, assistantId: 'gpt-4o-mini' }),
              message: { content },
            }),
            { Accept: 'text/event-stream' },
            controller.signal
          )
          .then((opened) => {
            if (expired) {
              void opened.cancel().catch(() => undefined);
              opened.releaseLock();
            }
            return opened;
          })
      );
    let openedReader: ReadableStreamDefaultReader<Uint8Array>;
    try {
      openedReader = await open();
    } catch (error) {
      // Only a definitive missing/expired conversation is safe to replay. Read failures
      // and all other HTTP errors may have already processed the turn.
      const status =
        error && typeof error === 'object' && 'status' in error ? error.status : undefined;
      if (
        !userId ||
        !session?.conversationId ||
        expired ||
        identityChanged ||
        useCommonStore.getState().user?.id !== userId ||
        (status !== 404 && status !== 410)
      )
        throw error;
      const previousRevision = session.revision;
      let replaced = false;
      useOnboardingStore.setState((state) => {
        const current = state.completionSessionsByUser[userId];
        if (current?.revision !== previousRevision) return state;
        const conversationsByTenant = { ...current.conversationsByTenant };
        delete conversationsByTenant[config.blocksKey];
        const revision = current.revision + 1;
        session = { revision };
        replaced = true;
        return {
          completionSessionsByUser: {
            ...state.completionSessionsByUser,
            [userId]: { revision, conversationsByTenant },
          },
        };
      });
      if (!replaced) throw error;
      openedReader = await open();
    }
    reader = openedReader;
    const decoder = new TextDecoder();
    let buffer = '';
    let text = '';
    let size = 0;
    while (!expired) {
      const { done, value } = await bounded(openedReader.read());
      if (done) {
        buffer += decoder.decode();
        if (buffer.trim()) throw new Error('Malformed or truncated completion stream');
        break;
      }
      size += value.byteLength;
      if (size > 262144) throw new Error('Completion stream exceeds size limit');
      buffer += decoder.decode(value, { stream: true });
      // Process frames separately to support mixed LF/CRLF and inspect errors the shared parser ignores.
      let separator: RegExpExecArray | null;
      while ((separator = /\r?\n\r?\n/.exec(buffer))) {
        const frame = buffer.slice(0, separator.index);
        buffer = buffer.slice(separator.index + separator[0].length);
        const event = frame
          .split(/\r?\n/)
          .find((line) => line.startsWith('event:'))
          ?.slice(6)
          .trim();
        if (event && FAILURE_TYPES.has(event.toUpperCase()))
          throw new Error(`Completion failed: ${event}`);
        let isAnswerDone = !!event && ANSWER_DONE_TYPES.has(event.toUpperCase());
        for (const line of frame.split(/\r?\n/).filter((line) => line.startsWith('data:'))) {
          const raw = line.slice(5).trim();
          if (!raw || raw === '[DONE]') continue;
          let record;
          try {
            record = JSON.parse(raw);
          } catch {
            throw new Error('Malformed completion stream');
          }
          for (const type of [
            record?.type,
            record?.streamingResponse?.type,
            record?.response?.type,
          ]) {
            if (typeof type === 'string' && FAILURE_TYPES.has(type.toUpperCase()))
              throw new Error(`Completion failed: ${type}`);
            if (typeof type === 'string' && ANSWER_DONE_TYPES.has(type.toUpperCase()))
              isAnswerDone = true;
          }
        }
        const parsed = readCompletionChunks(`${frame}\n\n`);
        if (
          userId &&
          session &&
          !identityChanged &&
          !expired &&
          useCommonStore.getState().user?.id === userId
        ) {
          for (const id of parsed.conversationIds)
            store.captureCompletionSession(userId, config.blocksKey, session.revision, id);
        }
        text += parsed.pieces.join('');
        if (isAnswerDone || isCompleteJsonObject(text)) return text;
      }
    }
    return text;
  } finally {
    signal?.removeEventListener('abort', onAbort);
    unsubscribe();
    clearTimeout(timer);
    controller.abort();
    if (reader) {
      // Cancellation must not make the bounded request wait on a broken transport.
      try {
        void reader.cancel().catch(() => undefined);
      } catch {
        /* already closed */
      }
      reader.releaseLock();
    }
  }
}
